<template>
  <Loading :show="isLoading">
    <table v-ga-section-view data-section-name="Contents" data-section-number="2">
      <thead>
        <tr>
          <th
            v-for="header in tableHeaders"
            :key="header.title"
            class="font2 md list-title"
            :class="kebabCase(header.title)"
            :style="{ ...(header.width ? { width: header.width } : {}) }"
          >
            {{ header.title }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="list.length === 0">
          <td colspan="7">
            {{
              searchType === 'character-name' && searchKeyword ? `No Search Character "${searchKeyword}"` : 'No Data'
            }}
          </td>
        </tr>
        <tr v-for="one in list" v-else :key="one.characterName">
          <td v-for="header in tableHeaders" :key="header.title" class="font sm">
            <RankingDataCell v-if="header.title != ''" :title="header.title" :value="one" />
            <template v-else>
              <img
                class="mobile-detail-menu-icon"
                src="@/assets/imgs/ranking/menu-sqared.png"
                @click="
                  () => {
                    rankingDetailOne = one
                    mobileDetailDlg = true
                  }
                "
              />
            </template>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="rankingFilter" class="updated-date">
      <span v-if="rankingFilter.key === 'weekly'">Weekly Update: Every Monday at 6:30 PM UTC</span>
      <span v-else-if="rankingFilter.key === 'monthly'">Monthly Update: 1st Day of Every Month at 6:00 PM UTC</span>
      <span v-else>Daily Update: Every Day at 4:00 PM UTC</span>
    </div>
    <Pagination
      :current-page="pageIndex"
      :items-per-page="itemsPerPage"
      :max-pagination="5"
      :total-count="totalListCount"
      @update:current-page="updatePageIndex($event)"
    />
  </Loading>
  <v-dialog v-model="mobileDetailDlg">
    <div v-if="rankingDetailOne" class="ranking-detail-dlg">
      <img class="close-btn" src="@/assets/imgs/common/X.png" @click="mobileDetailDlg = false" />
      <div class="character-frame">
        <img :src="rankingDetailOne.characterImgURL" />
      </div>
      <div class="title font2" v-text="rankingDetailOne.characterName" />
      <div class="job font" v-text="rankingDetailOne.jobName" />
      <div class="detail-list">
        <div
          v-for="header in tableHeadersOptions[headerKey].filter(
            (x) => !['character', 'character name', 'job'].includes(x.title.toLowerCase())
          )"
          :key="header.title"
          class="cell-frame"
        >
          <div class="header" v-text="header.title" />
          <div class="cell-data">
            <RankingDataCell :title="header.title" :value="rankingDetailOne" />
          </div>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
  import { tableHeadersForMobile, tableHeadersOptions } from '@/config/rankings'
  import { RankInfo, useRankingStore } from '@/store/rankings'
  import { useRankingOptionsStore } from '@/store/rankings/RankingOptions'
  import { storeToRefs } from 'pinia'
  import { kebabCase } from 'change-case'
  import { computed, ref, watch, watchEffect } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useDisplay } from 'vuetify'

  const router = useRouter()
  const route = useRoute()

  const { smAndDown: tabletAndMobile, mobile } = useDisplay()

  const {
    rankingRegion,
    rankingType,
    rankingFilter,
    rankingJob,
    rankingWorldType,
    pageIndex,
    optionSelectionComplete,
    searchType,
    searchKeyword,
  } = storeToRefs(useRankingOptionsStore())

  const itemsPerPage = 10

  const list = ref<RankInfo[]>([])
  const isLoading = ref(false)
  const totalListCount = ref(0)
  const store = useRankingStore()

  const mobileDetailDlg = ref(false)
  const rankingDetailOne = ref<RankInfo>()

  function updatePageIndex(pageIndex: number) {
    const querys = route.query
    delete querys.search_rank // conflict
    router.push({ query: { ...querys, page_index: pageIndex } })
  }

  watchEffect(async () => {
    if (!optionSelectionComplete.value) {
      totalListCount.value = 0
      return
    }
    const region = rankingRegion.value?.id?.toString()
    let type = rankingType.value?.key
    const filter = rankingJob.value ?? rankingFilter.value
    let id = filter?.id ?? filter?.key
    const rebootIndex = rankingWorldType.value?.id
    const pageIndexNum =
      searchType.value === 'ranks' ? Number(searchKeyword.value) : (pageIndex.value - 1) * itemsPerPage + 1
    if (!type) return
    // list.value = []

    // beginner (origin)
    if (id === -10) {
      type = 'originbeginner'
      id = 0
    }
    ///

    isLoading.value = true
    const { ranks, totalCount } = await store.fetch({
      region,
      type,
      id,
      rebootIndex,
      pageIndex: pageIndexNum,
      characterName: searchType.value === 'character-name' ? searchKeyword.value : '',
    })
    list.value = ranks ?? []
    totalListCount.value = totalCount
    isLoading.value = false
  })

  const headerKey = computed(
    () =>
      Object.keys(tableHeadersOptions).find((key) =>
        `${rankingType.value?.key}/${rankingFilter.value?.key}`.startsWith(key)
      ) ?? 'default'
  )

  const tableHeaders = computed(() => {
    if (!optionSelectionComplete.value) return []

    if (mobile.value) {
      const clone = [...tableHeadersForMobile[headerKey.value].slice(0, -1)]
      clone.push({
        title: '',
        width: '12%',
      })
      return clone
    }
    return (tabletAndMobile.value ? tableHeadersForMobile[headerKey.value] : tableHeadersOptions[headerKey.value]) ?? []
  })

  watch(mobile, () => {
    mobileDetailDlg.value = false
  })
</script>
<style lang="scss" scoped>
  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;

    thead {
      background-color: #f1f1f1;
      height: 60px;
      th {
        white-space: nowrap;
      }
    }
    tbody {
      td {
        padding: 2px 0;
        height: 100px;
        text-align: center;
        border-bottom: 1px solid #e7e7e7;
      }
    }
  }
  .list-title {
    text-transform: uppercase;
  }

  .desktop {
    .list-title {
      font-size: max(17px, 1rem);
    }
  }
  .mobile-detail-menu-icon {
    transition: scale 0.2s;
    &:active {
      scale: 0.9;
    }
  }
  .ranking-detail-dlg {
    background-color: white;
    border-radius: 10px;
    padding: pxToRemMobile(25);
    color: var(--content-text-color);
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: pxToRemMobile(120);
    position: relative;
    .close-btn {
      position: absolute;
      right: 0;
      top: -44px;
      cursor: pointer;
    }
    .character-frame {
      width: pxToRemMobile(200);
      aspect-ratio: 1/1;
      margin-top: pxToRemMobile(-140);
      position: relative;
      background-color: #d9d9d9;
      border: pxToRemMobile(10) solid white;
      img {
        width: 100%;
      }
    }
    .title {
      font-size: pxToRemMobile(36);
      font-weight: 700;
      margin: 0;
      line-height: pxToRemMobile(76);
    }
    .job {
      font-size: pxToRemMobile(18);
    }
    .detail-list {
      margin-top: pxToRemMobile(25);
      display: grid;
      width: 100%;
      grid-template-columns: 1fr 1fr;
      font-size: pxToRemMobile(18);
      gap: pxToRemMobile(10);
      .cell-frame {
        // height: 150px;
        border: 1px solid var(--border-color);
        .header {
          width: 100%;
          background-color: #f1f1f1;
          text-align: center;
          line-height: pxToRemMobile(42);
          text-transform: uppercase;
          font-family: 'Glegoo';
          font-weight: 700;
        }
        .cell-data {
          width: 100%;
          height: pxToRemMobile(108);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
        }
      }
    }
  }
  .updated-date {
    position: absolute;
    margin: 10px 0 0 10px;
    font-size: pxToRem(20);
    color: #919191;
    font-style: italic;
  }
  @media #{$tablet} {
    .updated-date {
      font-size: max(pxToRemTablet(16), 13px);
      position: relative;
    }
  }

  @media #{$mobile} {
    .updated-date {
      font-size: max(pxToRemMobile(12), 10px);
    }
    th {
      &.character {
        text-indent: -9999px;
      }
      &.tier {
        width: 15%;
      }
      &.name-job {
        width: 30% !important;
      }
      &.world {
        width: 12% !important;
      }
    }
  }
</style>
